import { Markdown } from '@/ui/Markdown';
import { OperationResultReference, TaskResultReference } from './ResultReference';
import { ConversationStages } from './ConversationStages';
import { SourceStages } from './SourceStages';
import type { ChatSourceSelection, ChatHistorySelection } from '@norum/contracts';
import { useEffect, useRef, useState } from 'react';
import * as RD from '@radix-ui/react-dialog';
import { Link, useLocation } from 'react-router';
import { contextFor } from './context';
import { AgentAbout, PastChatRow } from './PastChat';
import { failureOf } from '@/api/errors';
import { useChatAgents, useChatHistory, useChatTask, useOpenChat, useSay, useTranscript, useTwinPlan, useLatestTwinPlan, useChatMaterials, useUploadChatMaterial, useDriveChatMaterial, useDriveFolders, useDriveFiles, type ChatContext, type WireChatListing } from '@/api/hooks';
import { useToast } from '@/ui/Toast';
import { TeamPane } from './TeamPane';
import type { WireChatTurn, WireTwinPlanAnswer } from '@/api/wire';
import { HandoffCard, TwinPlanCard } from './TwinPlan';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { CONTROL_ROOM_NAME } from '@/app/nav';
import { useScope } from '@/app/scope';
import { Menu, MenuContent, MenuItem } from '@/ui/menu';
import { CHAT_MAX, CHAT_MIN, useFrame } from '@/app/frame';
import { Avatar, Button, IconButton, Kicker, SendButton } from '@/ui/primitives';
import { Tab, Textarea, Select } from '@/ui/fields';
import { Refusal } from '@/ui/Refusal';
import { twinOf } from '@/ui/people';
import { ScanVerdict } from '@/ui/drafts';
import { appPath } from '@/api/client';
import { useRecordPost } from '@/api/RecordContext';
import { TodayCallScope } from '@/api/TodayContext';
import { MeContext } from '@/app/MeContext';
import { technical, useTechnical } from '@/app/technical';
import type { TurnResult } from '@/api/wire';

export { contextFor };

/**
 * The side chat, on every page (frontend-sync §6).
 *
 * Its context is identifiers and never text: `{ page, …Id }`. Sending what
 * is on screen would put an unscanned sentence in front of a model, so the
 * agent resolves the identifiers itself through the reader that withholds
 * what the scanner blocked. The person is talking to their twin (§16), and
 * three answers are rendered as three things: an ordinary reply; a turn
 * that was recorded and not answered, with the reason; and a turn the
 * scanner marked, which is a badge and never a block.
 */
export function ChatRail() {
  const frame=useFrame(), me=useMeOrThrow(), location=useLocation();
  const drafts=useRef(new Map<string,string>());
  const department=location.pathname==='/today'?frame.workspaceContext?.department:null;
  const draftKey=JSON.stringify({humanId:me.humanId,department:department??me.department,context:frame.attached??frame.workspaceContext??contextFor(location.pathname,me.department)});
  const body=<ChatRailBody key={draftKey} drafts={drafts.current} draftKey={draftKey}/>;
  return department ? <MeContext.Provider value={{...me,department}}><TodayCallScope department={department}>{body}</TodayCallScope></MeContext.Provider> : body;
}
function ChatRailBody({drafts,draftKey}:{drafts:Map<string,string>;draftKey:string}) {
  const post=useRecordPost();
  const me = useMeOrThrow();
  const tech = useTechnical();
  const location = useLocation();
  const fullPage = location.pathname === '/chat';
  const frame = useFrame();
  const departmentLabel = useDepartmentLabel();
  const toast = useToast();
  // In the Control room the person talks to their own twin alone: a
  // department's agents answer in their department, never under its name.
  const { scope } = useScope(me);
  const listed = useChatAgents(me.department).data?.agents ?? [];
  const agents = scope.kind === 'control' ? listed.filter((a) => a.kind === 'twin') : listed;
  // Who the person is talking to (ADR-0089): their twin unless they picked
  // one of the department's agents. Kept by id so a refetch keeps the pick.
  const [agentId, setAgentId] = useState<string | null>(null);
  // A declared agent nobody provisioned has no id, so it never matches "none
  // chosen" — the twin does.
  const selected =
    (agentId !== null ? agents.find((a) => a.id === agentId) : undefined) ?? agents.find((a) => a.kind === 'twin') ?? null;
  const toAgent = selected?.kind === 'department' && selected.state === 'active' ? selected : null;
  const [view, setView] = useState<'thread' | 'history'>('thread');
  // Agents and teammates, one switch (P:2533-2539).
  const [tab, setTab] = useState<'agents' | 'team'>('agents');
  const [talkWith, setTalkWith] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [text, setText] = useState(() => drafts.get(draftKey) ?? '');
  const [sourceSelection, setSourceSelection] = useState<ChatSourceSelection | undefined>();
  const [historySelection, setHistorySelection] = useState<ChatHistorySelection | undefined>();
  useEffect(() => { drafts.set(draftKey,text); }, [drafts,draftKey,text]);
  const [unanswered, setUnanswered] = useState<{ said: WireChatTurn; problem: string; contextReport?: TurnResult['contextReport'] } | null>(null);
  // A twin's task, planned in the chat (Amendment 0011): the plan and the
  // words it was asked with, which a handoff to Nia sends as they were.
  const [planned, setPlanned] = useState<{ ask: string; answer: WireTwinPlanAnswer } | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const phoneReturnFocus = useRef<HTMLElement | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // What the person attached wins; then the calendar while it is open over
  // the page; then the page itself (P:2445-2477).
  const context: ChatContext =
    frame.attached ?? frame.workspaceContext ?? (frame.calendar !== 'closed' ? { page: 'calendar' } : contextFor(location.pathname, me.department));
  const contextKey = JSON.stringify(context);
  const openChat = useOpenChat(context, toAgent?.id ?? null);
  const history = useChatHistory(view === 'history');
  const task = useChatTask();
  const twinPlan = useTwinPlan();
  const latestPlan = useLatestTwinPlan(sessionId);
  const materials = useChatMaterials(selected?.kind === 'twin' ? sessionId : null);
  const uploadMaterial = useUploadChatMaterial();
  const driveMaterial = useDriveChatMaterial();
  const [pickingDrive, setPickingDrive] = useState(false);
  const [pastedMaterial, setPastedMaterial] = useState<string | null>(null);
  const visiblePlan = planned ?? (latestPlan.data?.plan ? { ask: latestPlan.data.plan.plan.originalRequest ?? latestPlan.data.plan.plan.subject, answer: latestPlan.data.plan } : null);

  // A session is opened with its context and its agent and keeps both: a new
  // page, or another agent, starts a new conversation rather than borrowing
  // one. Restoring a past chat sets both at once and is not a change of mind.
  const restoring = useRef(false);
  useEffect(() => {
    if (restoring.current) {
      restoring.current = false;
      return;
    }
    setSessionId(null);
    setReadOnly(false);
    setUnanswered(null);
    setPlanned(null);
  }, [contextKey, agentId, scope.kind]);

  // Into the Control room, a department's agent is let go: its chat answers
  // in its department, and would otherwise go on under the twin's name.
  useEffect(() => {
    if (scope.kind === 'control') setAgentId(null);
  }, [scope.kind]);

  // "Ask about this", "+", the twin's button: open, focus, and start from
  // the words given — which the person edits and sends, or doesn't.
  useEffect(() => {
    if (!frame.draft) return;
    const applied = `applied-draft:${me.humanId}:${frame.draft.at}`;
    if (drafts.has(applied)) return;
    drafts.set(applied, '');
    if (frame.draft.text) setText(frame.draft.text);
    setView('thread');
    requestAnimationFrame(() => input.current?.focus());
  }, [frame.draft]);
  // A notification's link, `?talk=<person>`, opens that conversation.
  const talkParam = new URLSearchParams(location.search).get('talk');
  const setChatOpen = frame.setChatOpen;
  // Keyed on the navigation too, so the same link opens the chat again after it was closed.
  useEffect(() => {
    if (!talkParam) return;
    setChatOpen(true);
    setTab('team');
    setTalkWith(talkParam);
  }, [talkParam, setChatOpen, location.key]);
  const transcript = useTranscript(sessionId);
  const say = useSay(sessionId);
  const [sending, setSending] = useState(false);
  const [sendFailure, setSendFailure] = useState<unknown>(null);

  const ensureSession = async (): Promise<string> => {
    if (sessionId) return sessionId;
    const opened = await openChat.mutateAsync();
    setSessionId(opened.session.id);
    return opened.session.id;
  };

  const send = async (said?: string) => {
    const words = (said ?? text).trim();
    if (!words || readOnly || sending) return;
    // Research is a confirmed task, not a chat answer with no web access.
    // The same composer serves page prompts and the side chat. Planning does
    // not execute a tool; the person still reads and confirms the plan.
    if (selected?.kind === 'twin' && sourceSelection === undefined && historySelection === undefined && isResearchTaskAsk(words)) {
      await planTask(undefined, words);
      return;
    }
    setSending(true);
    setSendFailure(null);
    try {
      const id = await ensureSession();
      // `useSay` was bound to the previous session id on first render; send
      // through the resolved one so the first turn is not lost to a null.
      const result = await post<TurnResult>(SAY, { sessionId: id, text: words, ...(sourceSelection === undefined ? {} : { sourceSelection }), ...(historySelection === undefined ? {} : { historySelection }) });
      // Cleared only once the turn is recorded: a refused or failed send
      // keeps what the person typed, and says why beneath it.
      setText('');
      if (!result.answered) setUnanswered({ said: result.said, problem: result.problem ?? 'no answer, and no reason given', ...(result.contextReport ? { contextReport: result.contextReport } : {}) });
      else setUnanswered(null);
    } catch (error) {
      setSendFailure(error);
    } finally {
      setSending(false);
      void transcript.refetch();
    }
  };

  // What a person asks a department agent to do becomes its task, by their
  // own press: nothing runs from a chat turn (ADR-0089).
  const makeTask = async () => {
    const words = text.trim();
    if (!words || !toAgent || readOnly) return;
    const id = await ensureSession();
    task.mutate(
      { sessionId: id, title: words },
      {
        onSuccess: (r) => {
          setText('');
          toast({
            text: r.existing
              ? `${r.agent.givenName} already has this as an open task, so it is not added twice.`
              : `${r.agent.givenName} has it as a task, and works it under the department’s assignments.`,
          });
        },
      },
    );
  };

  // What the person typed becomes a plan their twin writes and they read;
  // nothing runs until they confirm it (Amendment 0011, ADR-0139). Asked
  // again with a plan in view, it is a new version of that plan.
  const planTask = async (supersedes?: string, said?: string) => {
    const words = said?.trim() || text.trim() || (supersedes && visiblePlan ? visiblePlan.ask : '');
    if (!words || readOnly || twinPlan.isPending || selected?.kind !== 'twin') return;
    setSendFailure(null);
    try {
      const id = await ensureSession();
      const answer = await twinPlan.mutateAsync({ sessionId: id, ask: words, ...(supersedes ? { supersedes } : {}) });
      setText('');
      setPlanned({ ask: words, answer });
      void transcript.refetch();
    } catch {
      // Said beneath the transcript, from the mutation's own error.
    }
  };

  const uploadText = async (file?: File) => {
    if (!file || selected?.kind !== 'twin') return;
    if (file.size === 0) {
      toast({ text: 'Choose a nonempty text file.', tone: 'danger' });
      return;
    }
    const extension = file.name.toLowerCase().split('.').pop();
    const mediaType = extension === 'txt' ? 'text/plain' : extension === 'csv' ? 'text/csv' : extension === 'md' ? 'text/markdown' : null;
    if (!mediaType) {
      toast({ text: 'Choose a .txt, .csv or .md file for the twin to read.', tone: 'danger' });
      return;
    }
    try {
      const session = await ensureSession();
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
      await uploadMaterial.mutateAsync({ sessionId: session, filename: file.name, mediaType, dataBase64: btoa(binary) });
      toast({ text: `${file.name} is attached to this chat. Review the plan again to include it in a task.` });
    } catch {
      // The refusal beside the composer has the server's reason.
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const attachPaste = async () => {
    if (!pastedMaterial?.trim()) return;
    const bytes = new TextEncoder().encode(pastedMaterial);
    try {
      const session = await ensureSession();
      let binary = '';
      for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
      await uploadMaterial.mutateAsync({ sessionId: session, filename: 'Pasted material.txt', mediaType: 'text/plain', dataBase64: btoa(binary) });
      setPastedMaterial(null);
      toast({ text: 'Pasted material is attached. Review the plan again to include it in a task.' });
    } catch {
      // Show the server refusal beside the composer.
    }
  };

  const attachDrive = async (key: string, fileId: string) => {
    try {
      const session = await ensureSession();
      const answer = await driveMaterial.mutateAsync({ sessionId: session, key, fileId });
      setPickingDrive(false);
      toast({ text: `${answer.material.filename} is attached to this chat. Review the plan again to include it in a task.` });
    } catch {
      // Show the consent or tool refusal beside the picker.
    }
  };

  const restore = (listing: WireChatListing) => {
    const agent = listing.agentId ? agents.find((a) => a.id === listing.agentId) : undefined;
    // In the Control room only the twin's past chats reopen: a department
    // agent's answers in its department, never under the twin's name.
    if (scope.kind === 'control' && listing.agentId && !agent) return;
    const nextAgent = agent?.kind === 'department' ? agent.id : null;
    if (nextAgent !== agentId) restoring.current = true;
    setAgentId(nextAgent);
    setSessionId(listing.id);
    setReadOnly(listing.closedAt !== null);
    setUnanswered(null);
    setPlanned(null);
    setView('thread');
  };

  const startAfresh = () => {
    setSessionId(null);
    setReadOnly(false);
    setUnanswered(null);
    setPlanned(null);
    setView('thread');
  };

  // Closed, it is the twin's button in the header (TopBar).
  if (!frame.chatOpen && !fullPage) return null;

  const allTurns = transcript.data?.turns ?? [];
  const turns=allTurns.filter(t=>t.kind!=='activity');
  const activity=allTurns.filter(t=>t.kind==='activity');
  const room = scope.kind === 'control' ? CONTROL_ROOM_NAME : departmentLabel(me.department);
  const about = frame.head?.title ? `${frame.head.title} · ${room}` : room;
  const name = selected ? selected.givenName : frame.twin ?? 'Your twin';
  // A prospecting page is the department's: its agent answers about it, and
  // a twin is pointed to that agent rather than asked (F9, ADR-0089).
  const departmentPage = PROSPECTING_PAGES.has(context.page);
  const pageAgent = departmentPage ? (agents.find((a) => a.kind === 'department' && a.state === 'active' && a.givenName === 'Otis') ?? agents.find((a) => a.kind === 'department' && a.state === 'active') ?? null) : null;
  const delegateAgent = agents.find((a) => a.kind === 'department' && a.state === 'active' && a.id && a.givenName === 'Otis') ?? null;
  const movePlan = visiblePlan?.answer.kind === 'plan' ? visiblePlan.answer.plan : null;
  const starters =
    departmentPage && selected?.kind === 'department' ? (PAGE_STARTERS[context.page] ?? selected.starters) : selected?.starters.length ? selected.starters : STARTERS;
  const intro = selected?.intro ?? INTRO;

  const rail = (
    <aside
      aria-label={fullPage ? 'Chat' : 'Side chat'}
      className={`flex flex-col border-line bg-raised ${
        fullPage ? 'relative min-h-dvh min-w-0 flex-1' : frame.layout.phone
          ? 'fixed inset-0 z-50 h-[100dvh] w-full'
          : 'sticky top-0 h-dvh shrink-0 border-l'
      }`}
      style={fullPage || frame.layout.phone ? undefined : { width: frame.layout.chatWidth }}
    >
      {!fullPage && !frame.layout.phone && <WidthHandle width={frame.layout.chatWidth} onWidth={frame.setChatWidth} />}
      <header className="flex min-h-[68px] items-center gap-3 border-b border-line bg-page px-4 py-3">
        {tab === 'team' ? (
          <Avatar name="Team" size={34} />
        ) : (
          <Avatar name={name} size={34} agent={{ label: toAgent ? `${name} Agent` : twinOf(me.displayName) }} />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-dense font-semibold" data-testid="chat-with">{tab === 'team' ? 'Team' : name}</span>
          <span className="truncate text-label text-meta">
            {tab === 'team' ? 'The people you work with — each conversation is the two of yours' : `${selected?.title ?? 'Your twin · drafts, never sends'} · ${about}`}
          </span>
        </div>
        {tab === 'agents' && (
        <Button
          size="row"
          variant="tertiary"
          aria-pressed={view === 'history'}
          onClick={() => setView(view === 'history' ? 'thread' : 'history')}
        >
          {view === 'history' ? 'Back' : 'Past chats'}
        </Button>
        )}
        {fullPage ? (
          <Link to="/today" className="inline-flex min-h-tap items-center text-dense text-accent underline underline-offset-[3px]">Back to Today</Link>
        ) : (
          <>
            <Link to="/chat" onClick={() => frame.ask(undefined, context)} className="inline-flex min-h-tap items-center text-dense text-accent underline underline-offset-[3px]">
              Full chat
            </Link>
            <Button icon="hide" size="row" variant="tertiary" onClick={() => frame.setChatOpen(false)}>Hide</Button>
          </>
        )}
      </header>

      <div role="tablist" aria-label="Talk to" className="flex border-b border-line bg-page">
        {(['agents', 'team'] as const).map((t) => (
          <Tab key={t} selected={tab === t} onClick={() => setTab(t)} fill>
            {t === 'agents' ? 'Agents' : 'Team'}
          </Tab>
        ))}
      </div>

      {tab === 'team' ? (
        <TeamPane talkWith={talkWith} onTalkWith={setTalkWith} />
      ) : (
        <>
      {agents.length > 1 && (
        <div role="group" aria-label="Agents" className="flex gap-1.5 overflow-x-auto border-b border-line bg-page px-3 py-2">
          {agents.map((a) => {
            const on = (a.id ?? a.givenName) === (selected?.id ?? selected?.givenName);
            const usable = a.state === 'active' && a.id !== null;
            return (
              <button
                key={a.id ?? a.givenName}
                type="button"
                aria-pressed={on}
                disabled={!usable}
                title={
                  a.state === 'not_provisioned'
                    ? `${a.givenName} is declared in the ledger and not provisioned yet — there is nobody to talk to.`
                    : a.state === 'stopped'
                      ? `${a.givenName} is stopped.`
                      : `${a.givenName} — ${a.title}${a.purpose ? `. ${a.purpose}` : ''}`
                }
                onClick={() => setAgentId(a.kind === 'twin' ? null : a.id)}
                className={`flex min-h-tap shrink-0 items-center gap-2 border px-2.5 text-dense ${
                  on ? 'border-strong bg-raised text-ink' : 'border-control text-secondary hover:border-strong'
                } ${a.state === 'not_provisioned' ? 'border-dashed opacity-60' : ''} ${a.state === 'stopped' ? 'opacity-50' : ''} disabled:cursor-not-allowed`}
              >
                <span aria-hidden className={`block h-2 w-2 ${a.kind === 'twin' ? 'bg-accent' : 'bg-control'}`} />
                {a.givenName}
              </button>
            );
          })}
        </div>
      )}

      {view === 'history' ? (
        <ol className="m-0 flex flex-1 list-none flex-col gap-px overflow-y-auto bg-line p-0" aria-label="Past chats">
          {history.isPending && <li className="bg-raised p-4 text-dense text-meta">Reading your past chats…</li>}
          {history.data?.sessions.length === 0 && (
            <li className="bg-raised p-4 text-dense text-meta">No past chats yet. What you say here is kept, and listed here.</li>
          )}
          {history.data?.sessions.filter((listing) => scope.kind !== 'control' || !listing.agentId || agents.some((a) => a.id === listing.agentId)).map((listing) => (
            <PastChatRow key={listing.id} listing={listing} onRestore={() => restore(listing)} />
          ))}
        </ol>
      ) : (
        <ol className="m-0 flex flex-1 list-none flex-col gap-4 overflow-y-auto p-4" aria-live="polite" aria-label="Transcript">
          {transcript.hasNextPage && <li><button className="underline min-h-11" disabled={transcript.isFetchingNextPage} onClick={()=>void transcript.fetchNextPage()}>Load earlier conversation</button></li>}
          {activity.length>0 && <li><details><summary>Background activity ({activity.length})</summary>{activity.map(t=><p key={t.id}>{t.content}</p>)}</details></li>}
          {turns.length === 0 && !unanswered && !planned && (
            <li className="flex flex-col gap-3">
              <p className="m-0 text-dense leading-[1.6] text-secondary">
                {intro} I am told which page and which records you are on, never the words on the screen.
              </p>
              {selected && <AgentAbout agent={selected} />}
              {departmentPage && selected?.kind === 'twin' && pageAgent && (
                <p className="m-0 flex flex-wrap items-center gap-2 text-dense text-secondary" data-testid="ask-page-agent">
                  This page is {departmentLabel(me.department)}’s. {pageAgent.givenName} answers about it.
                  <Button size="row" onClick={() => setAgentId(pageAgent.id)}>
                    Ask {pageAgent.givenName}
                  </Button>
                </p>
              )}
              <div className="flex flex-col gap-2" role="group" aria-label="Start with">
                {starters.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    disabled={openChat.isPending || say.isPending || readOnly}
                    className="min-h-tap border border-control bg-page px-3.5 py-2.5 text-left text-dense text-ink hover:border-strong disabled:opacity-40"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </li>
          )}
          {turns.map((t) => (
            <Turn key={t.id} turn={t} twin={name} onContinue={!readOnly ? ()=>void send('Continue your previous answer from where it stopped, using the same sources. Do not repeat completed sections.') : undefined} />
          ))}
          {unanswered && !turns.some((t) => t.id === unanswered.said.id) && <Turn turn={unanswered.said} twin={name} />}
          {unanswered && (
            <li className="flex flex-col gap-1 border-l-2 border-warning bg-page px-3 py-2.5" data-testid="unanswered">
              <Kicker>Recorded, not answered</Kicker>
              <span className="text-dense text-secondary">{tech || !technical(unanswered.problem) ? unanswered.problem : 'The answer could not be written just now. What you said is kept; try again in a moment.'}</span>
              {unanswered.contextReport && <ul aria-label="Source coverage for unanswered request">{unanswered.contextReport.sources.map(source => <li key={source.id}>{materials.data?.materials.find(material => material.id === source.id)?.filename ?? 'Original source'}: {source.state}{source.reason ? ` — ${source.reason}` : ''}{source.range ? ` · characters ${source.range.from + 1}–${source.range.to}` : ''}</li>)}</ul>}
            </li>
          )}
          {visiblePlan?.answer.kind === 'plan' && (
            <TwinPlanCard
              key={visiblePlan.answer.planId}
              answer={visiblePlan.answer}
              twin={name}
              changing={twinPlan.isPending}
              onChange={() => void planTask(visiblePlan.answer.kind === 'plan' ? visiblePlan.answer.planId : undefined)}
              onMove={delegateAgent && movePlan ? { name: delegateAgent.givenName, act: () => {
                const ask = planned?.ask ?? `${movePlan.subject}. Deliver: ${movePlan.output}`;
                setText(ask);
                setAgentId(delegateAgent.id);
                requestAnimationFrame(() => input.current?.focus());
              } } : undefined}
            />
          )}
          {visiblePlan?.answer.kind === 'handoff' && <HandoffCard key={visiblePlan.ask} answer={visiblePlan.answer} ask={visiblePlan.ask} />}
          {(sendFailure !== null || openChat.isError || say.isError || task.isError || twinPlan.isError || uploadMaterial.isError) && (
            <li data-testid="send-refused">
              <Refusal compact failure={failureOf(sendFailure ?? openChat.error ?? say.error ?? task.error ?? twinPlan.error ?? uploadMaterial.error)} />
            </li>
          )}
        </ol>
      )}

      <div className="flex flex-col gap-2 border-t border-line bg-page p-3">
        {frame.attached && (
          <span className="flex items-center gap-2 self-start border border-dashed border-control px-2 py-1 text-label text-secondary">
            About: {contextLabel(frame.attached)}
            <IconButton label="Remove context" glyph="close" ghost onClick={frame.detach} />
          </span>
        )}
        {/* The wire, shown: exactly the identifiers the chat is told (§11). */}
        <div
          className="flex min-w-0 items-center gap-2 text-micro text-meta"
          title={tech ? `Sent as identifiers only: ${contextKey}. Page text is refused at the gateway, and your twin reads each record through the scanned reader.` : 'What you are looking at goes with your question, so it knows what you mean.'}
          data-testid="attached"
        >
          <span className="shrink-0 uppercase tracking-label">Attached</span>
          <span className="shrink-0 whitespace-nowrap border border-control px-1.5 py-0.5 text-label text-secondary">{contextLabel(context)}</span>
        </div>
        {selected?.kind === 'twin' && materials.data?.materials && materials.data.materials.length > 0 && (
          <div className="flex flex-wrap gap-1.5" aria-label="Attached materials">
            {materials.data.materials.map((material) => (
              <span key={material.id} className="border border-control bg-raised px-2 py-1 text-label">
                {material.filename}{material.scanVerdict === 'blocked' ? ' · blocked from model use' : ''}
              </span>
            ))}
            <SourceStages materials={materials.data.materials} staged={sourceSelection} choose={setSourceSelection} reset={() => setSourceSelection(undefined)} />
          </div>
        )}
        {!readOnly && <ConversationStages selected={historySelection} choose={setHistorySelection} lastSeq={allTurns.at(-1)?.seq ?? 0} />}
        {readOnly ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="chat-closed">
            <span className="min-w-[160px] flex-1 text-dense text-meta">This chat is closed. Its transcript stays; it does not reopen.</span>
            <Button icon="play" size="row" variant="secondary" onClick={startAfresh}>Start a new chat</Button>
          </div>
        ) : (
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            {/* One way in to everything beside sending: a person reads one
                "+" and the menu says what each does, instead of a row of
                buttons that crowds the box. */}
            <ComposerMenu
              items={[
                ...(selected?.kind === 'twin'
                  ? [
                      {
                        label: twinPlan.isPending ? 'Planning…' : 'Plan a task',
                        hint: text.trim() ? `${name} writes a plan from what you typed. Nothing runs until you confirm it.` : 'Type what you need first, then plan it.',
                        disabled: !text.trim() || twinPlan.isPending || openChat.isPending,
                        onSelect: () => void planTask(),
                      },
                    ]
                  : []),
                ...(toAgent
                  ? [
                      {
                        label: 'Make it a task',
                        hint: text.trim() ? `What you typed becomes ${toAgent.givenName}’s task. Nothing runs from the chat itself.` : 'Type what you need first, then make it a task.',
                        disabled: !text.trim() || task.isPending,
                        onSelect: () => void makeTask(),
                      },
                    ]
                  : []),
                ...(selected?.kind === 'twin'
                  ? [
                      {
                        label: uploadMaterial.isPending ? 'Attaching…' : 'Upload a text file',
                        hint: 'A .txt, .csv or .md file for this chat’s task.',
                        disabled: uploadMaterial.isPending || openChat.isPending,
                        onSelect: () => fileInput.current?.click(),
                      },
                      { label: 'Paste text', hint: 'Paste what your twin may use for this task.', onSelect: () => setPastedMaterial((value) => (value === null ? '' : null)) },
                      // The Drive picker lists a department's folders and reads as its
                      // agent: a department's work, never the Control room's.
                      ...(scope.kind !== 'control' ? [{ label: 'From Google Drive', hint: 'A Google Doc from a folder you can open.', onSelect: () => setPickingDrive((shown) => !shown) }] : []),
                    ]
                  : []),
              ]}
            />
            <Textarea
              ref={input}
              aria-label={toAgent ? `Ask ${toAgent.givenName}` : 'Ask your twin'}
              className="min-h-tap min-w-0 flex-1 resize-y border border-control bg-raised px-3 py-2 text-dense text-ink placeholder:text-meta"
              placeholder={toAgent ? `Ask ${toAgent.givenName}…` : 'Tell me what you need…'}
              value={text}
              rows={3}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <SendButton ready={!!text.trim()} disabled={!text.trim() || sending || openChat.isPending || say.isPending} />
            {selected?.kind === 'twin' && (
              <input
                ref={fileInput}
                type="file"
                accept=".txt,.csv,.md,text/plain,text/csv,text/markdown"
                aria-label="Choose a text document"
                className="hidden"
                onChange={(e) => void uploadText(e.target.files?.[0])}
              />
            )}
          </form>
        )}
        {selected?.kind === 'twin' && pastedMaterial !== null && !readOnly && (
          <div className="flex flex-col gap-2 border border-control p-3" aria-label="Paste material">
            <Textarea aria-label="Material text" value={pastedMaterial} onChange={(e) => setPastedMaterial(e.target.value)} rows={6} placeholder="Paste the text the twin may use for this task" />
            <Button type="button" size="row" disabled={!pastedMaterial.trim() || uploadMaterial.isPending} onClick={() => void attachPaste()}>
              Attach pasted text
            </Button>
          </div>
        )}
        {selected?.kind === 'twin' && pickingDrive && !readOnly && scope.kind !== 'control' && (
          <ChatDrivePicker department={me.department} busy={driveMaterial.isPending} failure={driveMaterial.error} onPick={(key, id) => void attachDrive(key, id)} />
        )}
      </div>
        </>
      )}
    </aside>
  );
  if (!fullPage && frame.layout.phone) return <RD.Root open={frame.chatOpen} onOpenChange={frame.setChatOpen}>
    <RD.Content aria-describedby={undefined} className="fixed inset-0 z-50 outline-none"
      onOpenAutoFocus={() => { phoneReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }}
      onCloseAutoFocus={event => { event.preventDefault(); if (phoneReturnFocus.current?.isConnected && !phoneReturnFocus.current.closest('[hidden]')) phoneReturnFocus.current.focus(); }}>
      <RD.Title className="sr-only">Assistant</RD.Title>
      {rail}
    </RD.Content>
  </RD.Root>;
  return rail;
}

interface ComposerAction {
  label: string;
  hint: string;
  disabled?: boolean;
  onSelect: () => void;
}

/** The composer's "+": planning, making a task and attaching material, in one menu. */
function ComposerMenu({ items }: { items: ComposerAction[] }) {
  if (items.length === 0) return null;
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button
          type="button"
          aria-label="More: plan a task or attach material"
          className="flex min-h-tap min-w-tap items-center justify-center self-start border border-control text-panel text-secondary hover:border-strong hover:text-ink"
        >
          +
        </button>
      </Menu.Trigger>
      <MenuContent align="start" side="top" className="w-[280px] max-w-[calc(100vw-32px)]">
        {items.map((item, i) => (
          <MenuItem key={item.label} last={i === items.length - 1} disabled={item.disabled === true} onSelect={item.onSelect} className="flex-col items-start justify-center gap-0.5 py-2 data-[disabled]:cursor-default data-[disabled]:opacity-60">
            <span className="text-dense text-ink">{item.label}</span>
            <span className="text-label text-meta">{item.hint}</span>
          </MenuItem>
        ))}
      </MenuContent>
    </Menu.Root>
  );
}

/** Select only a Google Doc from the department folder the person can list. */
function ChatDrivePicker({ department, busy, failure, onPick }: {
  department: string; busy: boolean; failure: unknown; onPick: (key: string, fileId: string) => void;
}) {
  const folders = useDriveFolders();
  const [key, setKey] = useState('');
  const files = useDriveFiles(key, undefined, key !== '');
  const choices = (folders.data?.folders ?? []).filter((f) => !f.onePer && f.department === department);
  return (
    <div className="flex flex-col gap-2 border border-control p-3" aria-label="Choose a Google Doc">
      <p className="text-dense">Choose a Google Doc from a folder you can access. Its text is saved in this chat under your Drive consent.</p>
      {folders.isError && <Refusal compact failure={failureOf(folders.error)} />}
      <Select aria-label="Drive folder" value={key} onChange={(e) => setKey(e.target.value)}>
        <option value="">Choose a folder…</option>
        {choices.map((f) => <option key={f.key} value={f.key}>{f.path}</option>)}
      </Select>
      {!folders.isLoading && choices.length === 0 && <p className="text-dense">No Drive folders are available for this department yet.</p>}
      {files.isError && <Refusal compact failure={failureOf(files.error)} />}
      {failure != null && <Refusal compact failure={failureOf(failure)} />}
      {files.data && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {files.data.files.filter((f) => f.mimeType === 'application/vnd.google-apps.document').map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-dense">
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <Button type="button" size="row" disabled={busy} onClick={() => onPick(key, f.id)}>Attach</Button>
            </li>
          ))}
          {files.data.files.every((f) => f.mimeType !== 'application/vnd.google-apps.document') && <li className="text-dense">No Google Docs in this folder.</li>}
        </ul>
      )}
    </div>
  );
}

/**
 * Clear research requests enter the plan flow when the person presses Send.
 * Questions *about* existing research stay ordinary chat. Ambiguous asks
 * still have the explicit Plan a task action.
 */
export function isResearchTaskAsk(ask: string): boolean {
  const words = ask.trim().toLowerCase();
  return /^(please\s+|could you\s+|can you\s+|i (?:need|want) (?:you|fen) to\s+)*(?:research|investigate|look into|find (?:current|latest|recent) (?:information|data|sources) (?:on|about|for))\b/.test(words);
}

const INTRO =
  'I work inside your account, so I have no address of my own. I read what you can read, draft what you ask, and never send — you do.';

/** The pages that are a department's own records (proposal 0009): its agent answers about them. */
const PROSPECTING_PAGES: ReadonlySet<ChatContext['page']> = new Set(['market', 'profile', 'territory', 'plan', 'prospect', 'signal', 'buyer', 'supplier', 'offer']);

/** What a person asks first on each, in their words; the agent reads the record's own fields. */
const PAGE_STARTERS: Partial<Record<ChatContext['page'], string[]>> = {
  incident: ['What happened, and what evidence was recorded?', 'What still needs a person’s decision?'],
  shortlist: ['Compare the recorded evidence for these candidates', 'What still needs verification before I decide?'],
  profile_change: ['Which evidence supports this profile change?', 'What would change if I approve it?'],
  introduction: ['What was asked, and what outcome was recorded?', 'What still needs a follow-up?'],
  index_entry: ['What does this entry record, and where did it come from?', 'Which details are unavailable or expired?'],
  market: ['Where does this market stand?', 'What is still not researched here?', 'Which route should a plan target, and why?'],
  profile: ['Which rules earn most of the fit?', 'Should the bar move, from what converted?'],
  territory: ['How much is left to sweep?', 'What came of what was found here?'],
  plan: ['Is this plan worth approving?', 'What did the dry run find?', 'How close is it to its budget?'],
  prospect: ['Why this one, and why now?', 'Draft the first email for me to send', 'Who could introduce us?'],
  supplier: ['What is still missing from them?', 'Draft the email asking for it'],
  offer: ['Which lines keep this offer from going out?', 'What do we need from the supplier first?'],
  request: ['Where is this request, and why?', 'What would move it on?'],
};

const STARTERS = [
  'What is on my day tomorrow?',
  'Find me 45 minutes this week',
  'Draft the follow-ups I owe',
  'What can you not do yet?',
];

const PAGE_LABEL: Record<ChatContext['page'], string> = {
  incident: 'This incident',
  shortlist: 'This shortlist',
  profile_change: 'This profile change',
  introduction: 'This introduction',
  index_entry: 'This index entry',
  institution: 'This institution programme',
  document: 'This document',
  brief: 'This prepared brief',
  today: 'Today',
  contact: 'This contact',
  company: 'This company',
  thread: 'This conversation',
  task: 'This task',
  project: 'This project',
  department: 'This department',
  calendar: 'Calendar',
  spec: 'This change',
  market: 'This market',
  profile: 'This profile',
  territory: 'This territory',
  plan: 'This plan',
  prospect: 'This prospect',
  signal: 'This signal',
  buyer: 'This buyer',
  supplier: 'This supplier',
  offer: 'This offer',
  request: 'This request',
};

export function contextLabel(c: ChatContext): string {
  return PAGE_LABEL[c.page];
}

/**
 * The prototype's bubbles (P:1333-1344): what you said, filled and to the
 * right; what your twin said, plain under its name and mark. A scan verdict
 * is a line with a square, never a block.
 */
function Turn({ turn, twin, onContinue }: { turn: WireChatTurn; twin: string | null; onContinue?: (()=>void)|undefined }) {
  const frame = useFrame();
  const tech = useTechnical();
  const mine = turn.role === 'human';
  const flagged = turn.scanVerdict !== 'clean' && turn.scanVerdict !== 'pending';
  if (mine) {
    return (
      <li className="flex flex-col items-end gap-1">
        <span className="text-label text-meta">Turn {turn.seq}</span>
        <p className="m-0 max-w-[88%] whitespace-pre-wrap bg-[var(--btn-bg)] px-3.5 py-2.5 text-dense text-[var(--btn-fg)]">{turn.content}</p>
        {flagged && <ScanVerdict verdict={turn.scanVerdict} />}
      </li>
    );
  }
  return (
    <li className="flex flex-col gap-1.5">
      <span className="text-label text-meta">Turn {turn.seq}</span>
      <span className="flex items-center gap-2 text-label font-semibold uppercase tracking-label text-secondary">
        <span aria-hidden className="block h-2 w-2 bg-accent" />
        {twin ?? 'Your twin'}
        {tech && turn.routeModel && (
          <span className="font-normal normal-case tracking-normal text-meta">
            · {turn.routeModel}
            {turn.routeRegion ? ` · ${turn.routeRegion}` : ''}
          </span>
        )}
      </span>
      <Markdown text={turn.content} openRecord={frame.workspaceOpener ?? undefined} />
      {turn.completionStatus==='length' && <div role="status"><p>The provider stopped before completing the answer{turn.contextReport?.completionReason ? ` (${turn.contextReport.completionReason})` : ''}. The text above is incomplete. You can request continuation or choose explicit document stages.</p>{onContinue && <button className="underline min-h-11" onClick={onContinue}>Continue answer</button>}</div>}
      {turn.completionStatus==='blocked' && <p role="status">The provider held this response{turn.contextReport?.completionReason ? ` (${turn.contextReport.completionReason})` : ''}. Any returned text above remains incomplete.</p>}
      {turn.contextReport && <details><summary>Sources and conversation used</summary><p>{turn.contextReport.history.included} earlier turns included.{turn.contextReport.history.olderAvailable?' Older turns remain outside this request.':''}</p><ul>{turn.contextReport.sources.map(s=><li key={s.id}>{s.title ?? s.id}: {s.state}{s.reason?' — '+s.reason:''}{s.range ? ` · explicit characters ${s.range.from + 1}–${s.range.to}` : ''}</li>)}</ul></details>}
      {(turn.resultRefs??[]).map(r => r.kind === 'operation' ? <OperationResultReference key={r.id} id={r.id}/> : <TaskResultReference key={r.id} id={r.id}/>)}
      <details><summary>Original response text</summary><pre className="whitespace-pre-wrap break-words">{turn.content}</pre></details>
      {(turn.prepared ?? []).some((p) => p.eventId) && (
        // A hold the twin placed is on the person's day; this is where they
        // look at it and press Send invitations (ADR-0119).
        <Link to="/today" className="text-dense text-accent underline underline-offset-[3px]" data-testid="prepared-hold">
          See the hold on your day
        </Link>
      )}
      {flagged && <ScanVerdict verdict={turn.scanVerdict} />}
    </li>
  );
}

const SAY = appPath('/v1/chat/say');

/**
 * The chat's left edge, dragged to make it wider or narrower (outreach
 * A-17). Arrow keys move it too, and a double-click puts it back. The
 * width is kept in this browser, as the rail's hidden state is.
 */
function WidthHandle({ width, onWidth }: { width: number; onWidth: (width: number | null) => void }) {
  const start = useRef<{ x: number; width: number } | null>(null);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Chat width"
      aria-valuenow={width}
      aria-valuemin={CHAT_MIN}
      aria-valuemax={CHAT_MAX}
      tabIndex={0}
      data-testid="chat-width-handle"
      className="absolute inset-y-0 left-0 z-10 w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-accent/40 focus-visible:bg-accent/60 focus-visible:outline-none"
      onPointerDown={(e) => {
        start.current = { x: e.clientX, width };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (start.current) onWidth(start.current.width + (start.current.x - e.clientX));
      }}
      onPointerUp={(e) => {
        start.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onDoubleClick={() => onWidth(null)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') onWidth(width + 16);
        else if (e.key === 'ArrowRight') onWidth(width - 16);
        else return;
        e.preventDefault();
      }}
    />
  );
}
